<template>
    <b-modal
        no-fade
        :id="modalRef"
        :ref="modalRef"
        :title="title"
        :lazy="true"
        title-tag="h2"
        no-close-on-backdrop
        no-close-on-esc
        @shown="shown"
    >
        <input
            type="hidden"
            name="ritem_id"
            :value="
                (selectedInvoiceItem && selectedInvoiceItem.return_item_id) ||
                null
            "
        />
        <input
            type="hidden"
            name="item_quantity"
            :value="
                (selectedInvoiceItem && selectedInvoiceItem.quantity) || null
            "
        />
        <input
            type="hidden"
            name="is_record"
            :value="
                (selectedInvoiceItem && selectedInvoiceItem.is_record) || null
            "
        />

        <b-form-group label-for="item" label-cols="3" label="Item:">
            <div class="p-2" v-if="returnDetails.status">
                {{ data.item ? data.item.name : "" }}
            </div>
            <v-select
                v-else
                ref="vselectInvoiceItem"
                placeholder="Item (required)"
                name="item"
                v-validate="'required'"
                v-model="data.item"
                :options="itemOptions"
                :selectOnTab="true"
                :clearSearchOnSelect="true"
                :class="{ 'border-danger': errors.has('item') }"
                @search="onSearch"
                :disabled="isSaving"
            >
            </v-select>
        </b-form-group>
        <b-form-group label-for="returned" label-cols="3" label="Return:">
            <div class="p-2" v-if="returnDetails.status">
                {{ data.returned }}
                <input type="hidden" name="quantity" :value="data.returned" />
            </div>
            <b-input
                v-else
                v-model="data.returned"
                :class="{
                    'border-danger': errors.has('returned')
                }"
                v-validate="
                    !_.isEmpty(selectedInvoiceItem)
                        ? {
                              required: true,
                              regex: /^(?=.*[1-9])\d+(\.\d+)?$/, //anything greater than zero
                              max_value: selectedInvoiceItem.quantity_balance
                          }
                        : ''
                "
                name="returned"
                type="text"
                :placeholder="returnedPlaceholder"
                :disabled="isSaving"
            />
        </b-form-group>

        <b-form-group label-for="restocked" label-cols="3" label="Restock:">
            <div class="p-2" v-if="returnDetails.status">
                {{ data.restocked }}
                <input type="hidden" name="restock" :value="data.restocked" />
            </div>
            <b-input
                v-else
                v-model="data.restocked"
                :class="{
                    'border-danger': errors.has('restocked')
                }"
                v-validate="
                    !_.isEmpty(selectedInvoiceItem)
                        ? {
                              min_value: 0,
                              max_value: data.returned
                          }
                        : null
                "
                name="restocked"
                type="text"
                placeholder="Quantity Restocked (optional)"
                :disabled="isSaving"
            />
        </b-form-group>
        <b-form-group label-for="record" label-cols="3" label="Records:">
            <div class="p-2" v-if="returnDetails.status">
                <span v-if="data.record == 1"
                    >Don't deactivate medical record</span
                >
                <span v-else-if="data.record == 2"
                    >Deactivate medical record</span
                >
                <span v-else>Not Available</span>
            </div>
            <select
                v-else
                v-model="data.record"
                :class="{ 'border-danger': errors.has('record') }"
                id="record"
                name="record"
                class="form-control"
                aria-invalid="false"
                :disabled="isRecordDisabled || isSaving || disableRecord"
            >
                <option :value="1">Don't deactivate medical record</option>
                <option :value="2">Deactivate medical record</option>
            </select>
        </b-form-group>
        <b-form-group label-for="reason" label-cols="3" label="Reason:">
            <select
                v-model="data.reason"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('reason') }"
                id="reason"
                name="reason"
                class="form-control"
                :disabled="isSaving"
            >
                <option :value="1">Damaged/defective</option>
                <option :value="2">Changed Mind</option>
                <option :value="3">Dissatisfied</option>
                <option :value="4">Mistake</option>
                <option :value="5">Bad Fit</option>
                <option :value="6">Recall</option>
                <option :value="7">Other</option>
            </select>
        </b-form-group>
        <b-form-group label-for="notes" label-cols="3" label="Notes:">
            <b-textarea
                v-model="data.notes"
                name="notes"
                placeholder="Notes (optional)"
                :disabled="isSaving"
            />
        </b-form-group>

        <template v-slot:modal-footer v-if="isSaving">
            <loader :margin="1" />
        </template>
        <template v-slot:modal-footer="{ cancel }" v-else>
            <b-button class="mr-auto" variant="secondary" @click="cancel">
                Cancel
            </b-button>

            <!-- save group button for ADD mode -->
            <b-button-group v-if="isEnabledButtonSaveGroup">
                <b-dropdown
                    v-if="!isContinue"
                    @click="save(false)"
                    left
                    split
                    text="Save + Close"
                    variant="primary"
                    :disabled="isSaving || isLoading"
                >
                    <b-dropdown-item @click="save(true)">
                        Save + New
                    </b-dropdown-item>
                </b-dropdown>
                <b-dropdown
                    v-else
                    @click="save(true)"
                    left
                    split
                    text="Save + New"
                    variant="primary"
                    :disabled="isSaving || isLoading"
                >
                    <b-dropdown-item @click="save(false)">
                        Save + Close
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>

            <!-- save button for EDIT mode -->
            <b-button variant="primary" @click="saveChanges(false)" v-else>
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
/**
 * @name AddNewItemReturn
 * @see /apps/assets/js/module/billing/return.js
 * @see /apps/application/views/staff/billing/return/return/sub/item/wizard/create_update.php
 *
 */

import Loader from "@/module/commons/loader";
import vSelect from "vue-select";
import { debounce } from "lodash";
import BillingReturnServices from "@/services/billing/billing-return";

const ADD = "ADD";
const EDIT = "EDIT";

export default {
    components: {
        Loader,
        vSelect
    },
    props: {
        modalRef: {
            type: String,
            required: true
        },
        returnDetails: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            mode: ADD,
            title: "New Item",
            itemOptions: [],
            isSaving: false,
            isLoading: false,
            isContinue: false,
            selectedInvoiceItem: null,
            data: {
                item: null,
                returned: 0,
                restocked: 0,
                record: 1,
                reason: 1,
                notes: null
            },
            disableRecord: false,
            itemLoad: 0,
            selectedReturnItemId: null
        };
    },
    computed: {
        isEnabledButtonSaveGroup() {
            return this.mode == ADD;
        },
        returnedPlaceholder() {
            return `Quantity Returned (required, maximum ${
                this.selectedInvoiceItem
                    ? this.selectedInvoiceItem.quantity_balance
                    : ""
            })`;
        },
        isRecordDisabled() {
            if (this.selectedInvoiceItem) {
                if (
                    this.data.returned !=
                    this.selectedInvoiceItem.quantity_balance
                ) {
                    return true;
                } else {
                    return !this.selectedInvoiceItem.is_record;
                }
            }
            return false;
        }
    },
    watch: {
        "data.item"(value) {
            if (value) {
                let params = { id: value.id };
                if (this.mode === EDIT && this.itemLoad === 0) {
                    params.action = "Edit";
                    params.return_item_id = value.return_item_id;
                }
                this.isLoading = true;
                BillingReturnServices.getInvoiceItem(params)
                    .then(res => {
                        if (
                            res &&
                            res.code === 200 &&
                            res.resource &&
                            res.resource
                        ) {
                            if (
                                (this.mode === EDIT && this.itemLoad > 0) ||
                                this.mode === ADD
                            ) {
                                this.data.returned = null;
                            }

                            this.selectedInvoiceItem = res.resource;
                            this.$validator.reset();
                        }
                        this.isLoading = false;
                    })
                    .then(() => {
                        this.itemLoad++;
                    });
            }
        }
    },
    methods: {
        open(returnItem = null) {
            let self = this;
            self.selectedReturnItemId = null;
            self.disableRecord = false;
            self.itemLoad = 0;

            if (returnItem) {
                self.mode = EDIT;
                // Populate data and for edit mode
                self.title = "Edit Item";
                self.selectedReturnItemId = returnItem.id;
                self.data.item = {
                    id: returnItem.invoice_item_id,
                    name: returnItem.item.name,
                    label: returnItem.item.name,
                    return_item_id: returnItem.id
                };
                self.data.returned = returnItem.quantity;
                self.data.restocked = returnItem.restock;
                self.data.reason = returnItem.type;
                self.data.notes = returnItem.note;
                self.data.record = returnItem.record_status;
            } else {
                self.mode = ADD;
                self.title = "New Item";
            }

            self.$nextTick(() => {
                self.$bvModal.show(self.modalRef);
            });
        },
        reset() {
            this.selectedInvoiceItem = null;
            this.data.item = null;
            this.data.returned = 0;
            this.data.restocked = 0;
            this.data.record = 1;
            this.data.reason = 1;
            this.data.notes = null;
            this.$validator.reset();
        },
        shown() {
            if (this.mode === ADD) {
                this.$refs.vselectInvoiceItem.$el
                    .querySelector("input")
                    .focus();
            }
        },
        async save(isContinue) {
            let valid = await this.$validator.validateAll();
            let self = this;

            if (!valid) {
                return false;
            }

            const params = {
                return_id: self.returnDetails.id,
                invoice_item_id: self.data.item.id,
                quantity: Number(self.data.returned),
                restock: Number(self.data.restocked),
                record: self.data.record,
                type: self.data.reason,
                note: self.data.notes
            };

            self.isSaving = true;
            BillingReturnServices.createReturnItem(params)
                .then(res => {
                    if (res.code == 200) {
                        self._message(res.message);
                        self.$emit("reload");
                        self.isContinue = isContinue;
                        if (isContinue) {
                            self.reset();
                        } else {
                            self.$bvModal.hide(self.modalRef);
                        }
                    } else if (res.code == 400) {
                        self._message(
                            self.processMessage(res.message),
                            "danger"
                        );
                    }
                })
                .catch(e => {
                    self._message(e.message, "danger");
                })
                .finally(() => {
                    self.isSaving = false;
                });
        },
        async saveChanges() {
            let valid = await this.$validator.validateAll();
            let self = this;

            if (!valid) {
                return false;
            }

            const params = {
                return_id: self.returnDetails.id,
                invoice_item_id: self.data.item.id,
                quantity: Number(self.data.returned),
                restock: Number(self.data.restocked),
                record: self.data.record,
                type: self.data.reason,
                note: self.data.notes
            };

            self.isSaving = true;
            BillingReturnServices.editReturnItem(
                self.selectedReturnItemId,
                params
            )
                .then(res => {
                    if (res.code == 200) {
                        self._message(res.message);
                        self.$emit("reload");

                        self.$bvModal.hide(self.modalRef);
                    } else if (res.code == 400) {
                        self._message(
                            self.processMessage(res.message),
                            "danger"
                        );
                    }
                })
                .catch(e => {
                    self._message(e.message, "danger");
                })
                .finally(() => {
                    self.isSaving = false;
                });
        },
        processMessage(message) {
            if (typeof message === "object") {
                let fullMessage = "";

                for (const key in message) {
                    fullMessage = message[key] + " ";
                }

                return fullMessage;
            }

            return message;
        },
        // search item
        onSearch(value) {
            this.query = value;
            if (!value) {
                return;
            }

            this.fetchOptions(value, this);
        },
        fetchOptions: debounce(async (value, self) => {
            const params = {
                search_key: value,
                client_id: self.returnDetails.client_id
            };
            self.itemOptions = [];
            BillingReturnServices.getInvoiceItems(params).then(res => {
                if (res.code === 200) {
                    self.itemOptions = res.resources.map(r => ({
                        ...r,
                        label: r.name
                    }));
                }
            });
        }, 350)
    }
};
</script>
